import React, { useState, useEffect } from 'react';
import { Box, Grid, Typography, CircularProgress } from '@mui/material';
import { useKeycloak } from '@react-keycloak/web';
import { useUser } from '../../contexts/UserContext';
import ServerHealthChart from '../dashboard/ServerHealthChart';
import Notifications from '../dashboard/Notifications';
import SystemActivity from '../dashboard/SystemActivity';
import DeployedAgents from '../dashboard/DeployedAgents';
import ServerGridDisplay from '../dashboard/ServerGridDisplay';
// import TrainingJobs from '../dashboard/TrainingJobs';
import { getNodes, getNodeById, transformNodeToDisplayFormat } from '../api/services/computeService/NodeService';
import { getClientNodes } from '../api/services/computeService/ClientNodeService';
import { getDepartmentNodes } from '../api/services/computeService/DepartmentNodeService';
import { getTeamNodes } from '../api/services/computeService/TeamNodeService';
import { getUserNodes } from '../api/services/computeService/UserNodeService';
import { getAgentList } from '../api/services/agent-studio/AgentManagement';

export default function Dashboard() {
  const [selectedMetric, setSelectedMetric] = useState('status');
  const [selectedTimeframe, setSelectedTimeframe] = useState('hour');
  const [servers, setServers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { keycloak } = useKeycloak();
  const { hasAccess, isSuperAdmin, userPermissions, user } = useUser();

  const canViewNodes = () => {
    if (isSuperAdmin) return true;
    if (userPermissions) {
      if (userPermissions.teamIds && userPermissions.teamIds.length > 0) return true;
      if (userPermissions.departmentIds && userPermissions.departmentIds.length > 0) return true;
      if (userPermissions.clientIds && userPermissions.clientIds.length > 0) return true;
    }
    if (user && user.id) return true;
    return false;
  };

  const fetchNodes = async () => {
    if (!keycloak.token) return;

    try {
      try {
        await keycloak.updateToken(30);
      } catch (tokenError) {
        console.error('Token refresh failed:', tokenError);
        keycloak.logout({ redirectUri: window.location.origin });
        return;
      }

      let nodeIds = [];

      if (isSuperAdmin) {
        const response = await getNodes({
          paginationParams: { page: 0, page_size: 200 },
          token: keycloak.token
        });

        if (response.success && response.data.nodes) {
          nodeIds = response.data.nodes.map(node => node.id || node.nodeId).filter(id => id);
        }
      } else if (userPermissions) {
        if (userPermissions.teamIds && userPermissions.teamIds.length > 0) {
          const response = await getTeamNodes({
            teamId: userPermissions.teamIds[0],
            token: keycloak.token
          });
          if (response.success && response.data.nodeIds) {
            nodeIds = response.data.nodeIds;
          }
        } else if (userPermissions.departmentIds && userPermissions.departmentIds.length > 0) {
          const response = await getDepartmentNodes({
            departmentId: userPermissions.departmentIds[0],
            token: keycloak.token
          });
          if (response.success && response.data.nodeIds) {
            nodeIds = response.data.nodeIds;
          }
        } else if (userPermissions.clientIds && userPermissions.clientIds.length > 0) {
          const response = await getClientNodes({
            clientId: userPermissions.clientIds[0],
            token: keycloak.token
          });
          if (response.success && response.data.nodeIds) {
            nodeIds = response.data.nodeIds;
          }
        } else if (user && user.id) {
          const response = await getUserNodes({
            userId: user.id,
            token: keycloak.token
          });
          if (response.success && response.data.nodeIds) {
            nodeIds = response.data.nodeIds;
          }
        }
      }

      if (nodeIds.length > 0) {
        const detailedNodes = await Promise.all(
          nodeIds.map(async (nodeId) => {
            try {
              const nodeResponse = await getNodeById(nodeId, keycloak.token);
              if (nodeResponse.success && nodeResponse.data) {
                return transformNodeToDisplayFormat(nodeResponse.data);
              }
              return null;
            } catch (error) {
              console.warn(`Failed to fetch details for node ${nodeId}:`, error);
              return null;
            }
          })
        );

        const validNodes = detailedNodes.filter(node => node !== null);
        setServers(validNodes);
      } else {
        setServers([]);
      }
    } catch (error) {
      console.error('Error fetching nodes:', error);
      setError('Failed to fetch server data');
    }
  };

  // Function to fetch agent data
  const fetchAgents = async () => {
    if (!keycloak.token) return;

    try {
      try {
        await keycloak.updateToken(30);
      } catch (tokenError) {
        console.error('Token refresh failed:', tokenError);
        keycloak.logout({ redirectUri: window.location.origin });
        return;
      }
      const response = await getAgentList(keycloak.token);
      
      if (response.success && response.data) {
        const agents = Array.isArray(response.data.agents) ? response.data.agents : response.data;
        setAgents(agents);
      }
    } catch (error) {
      console.error("Error fetching agents:", error);
      setError('Failed to fetch agent data');
    }
  };

  // Load data when component mounts
  useEffect(() => {
    const initializeDashboard = async () => {
      if (!keycloak.authenticated) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const promises = [fetchAgents()];
        // Fetch nodes if user can view them based on permissions
        if (canViewNodes()) {
          promises.push(fetchNodes());
        }
        await Promise.all(promises);
      } catch (error) {
        console.error('Error initializing dashboard data:', error);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    initializeDashboard();
  }, [keycloak.authenticated, keycloak.token, hasAccess, isSuperAdmin, userPermissions, user]);

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
        backgroundColor: 'transparent'
      }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography sx={{ color: 'text.primary' }}>Loading dashboard...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      height: { xs: 'auto', lg: 'calc(100vh - 64px - 48px)' },
      px: { xs: 2, sm: 3 },
      pt: 2,
      pb: { xs: 2, sm: 3 }
    }}>
      <Grid container spacing={{ xs: 1.5, sm: 2, md: 2.5 }} sx={{ height: '100%' }}>
        {canViewNodes() && (
          <Grid item xs={12} lg={4} sx={{ display: 'flex', flexDirection: 'column', height: { lg: '100%' } }}>
            <Box sx={{
              height: { xs: '300px', sm: '350px', lg: 'calc(50% - 12px)' },
              mb: { xs: 1.5, sm: 2, lg: 2.5 }
            }}>
              <ServerHealthChart
                selectedTimeframe={selectedTimeframe}
                setSelectedTimeframe={setSelectedTimeframe}
                servers={servers}
                hasAccess={hasAccess}
              />
            </Box>

            <Box sx={{
              height: { xs: '300px', sm: '350px', lg: 'calc(50% - 12px)' }
            }}>
              <ServerGridDisplay
                selectedMetric={selectedMetric}
                setSelectedMetric={setSelectedMetric}
                servers={servers}
                hasAccess={hasAccess}
              />
            </Box>
          </Grid>
        )}
        
        <Grid item xs={12} lg={canViewNodes() ? 4 : 6} sx={{ display: 'flex', flexDirection: 'column', height: { lg: '100%' } }}>
          <Box sx={{
            height: canViewNodes()
              ? { xs: '400px', sm: '450px', lg: 'calc(66.67% - 16px)' }
              : { xs: '500px', sm: '600px', lg: 'calc(100vh - 64px - 96px)' },
            mb: { xs: 1.5, sm: 2, lg: 2.5 }
          }}>
            <Notifications />
          </Box>

          {canViewNodes() && (
            <Box sx={{
              height: { xs: '250px', sm: '300px', lg: 'calc(33.33% - 16px)' }
            }}>
              <SystemActivity servers={servers} agents={agents} />
            </Box>
          )}
        </Grid>
        
        <Grid item xs={12} lg={canViewNodes() ? 4 : 6} sx={{ display: 'flex', flexDirection: 'column', height: { lg: '100%' } }}>
          <Box sx={{
            height: { xs: '500px', sm: '600px', lg: '100%' }
          }}>
            <DeployedAgents agents={agents} hasAccess={hasAccess} />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
